import { SHAPE_SIZES } from '../model';
import { BOX_DISPLAY_SCALE, sizeForShape } from '../display';
import { STROKE_WIDTH } from '../network/constants';

export const NODE_BORDER_WIDTH = STROKE_WIDTH;

/**
 * Rendered size of a node. `imageSize` is the natural size of the node's image, when known;
 * image nodes scale so that their shorter side matches the default image size,
 * unless the user has resized them.
 */
export const nodeSize = (node, imageSize) => {
  switch (node.shape) {
    case 'ellipse':
      return sizeForShape(node, 'ellipse');
    case 'box': {
      const { width, height } = sizeForShape(node, 'box');
      return { width: width * BOX_DISPLAY_SCALE, height: height * BOX_DISPLAY_SCALE };
    }
    case 'image': {
      if (node.resized && node.width > 0 && node.height > 0) {
        return { width: node.width, height: node.height };
      }
      const base = SHAPE_SIZES.image.width;
      if (imageSize && imageSize.width > 0 && imageSize.height > 0) {
        const ratio = imageSize.width / imageSize.height;
        return ratio >= 1
          ? { width: base * ratio, height: base }
          : { width: base, height: base / ratio };
      }
      return { width: base, height: base };
    }
    default:
      return SHAPE_SIZES[node.shape] || SHAPE_SIZES.dot;
  }
};

export const containsPoint = (node, size, px, py) => {
  const dx = px - node.x;
  const dy = py - node.y;
  const a = size.width / 2;
  const b = size.height / 2;
  switch (node.shape) {
    case 'dot':
      return dx * dx + dy * dy <= a * a;
    case 'ellipse':
      return (dx * dx) / (a * a) + (dy * dy) / (b * b) <= 1;
    default:
      return Math.abs(dx) <= a && Math.abs(dy) <= b;
  }
};

/** Distance from the node centre to its border in direction `angle` (radians, screen axes). */
export const radiusAt = (node, size, angle) => {
  const a = size.width / 2;
  const b = size.height / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  switch (node.shape) {
    case 'dot':
      return a;
    case 'ellipse':
      return (a * b) / Math.sqrt((b * cos) ** 2 + (a * sin) ** 2);
    default: {
      const rx = Math.abs(cos) < 1e-9 ? Infinity : a / Math.abs(cos);
      const ry = Math.abs(sin) < 1e-9 ? Infinity : b / Math.abs(sin);
      return Math.min(rx, ry);
    }
  }
};

export const borderPoint = (node, size, angle) => {
  const r = radiusAt(node, size, angle);
  return { x: node.x + Math.cos(angle) * r, y: node.y + Math.sin(angle) * r };
};

export const nodeBounds = (node, size) => ({
  x0: node.x - size.width / 2,
  y0: node.y - size.height / 2,
  x1: node.x + size.width / 2,
  y1: node.y + size.height / 2,
});

/** Union of node bounds, or null when there are no nodes. */
export const boundsOf = (nodes, sizeOf) => nodes.reduce((acc, node) => {
  const b = nodeBounds(node, sizeOf(node));
  if (!acc) return { ...b };
  return {
    x0: Math.min(acc.x0, b.x0),
    y0: Math.min(acc.y0, b.y0),
    x1: Math.max(acc.x1, b.x1),
    y1: Math.max(acc.y1, b.y1),
  };
}, null);
